import {
  buildDashboardRoutePath,
  ORGANIZATIONS_QUERY_KEY,
} from "@frontend-shared/constants";
import type {
  IAgentAutoPausedNotificationData,
  IAgentRunFailedNotificationData,
  IAssistantAccessRevokedNotificationData,
  IAssistantCreatedNotificationData,
  IAssistantEditedNotificationData,
  IAssistantLeftNotificationData,
  IAssistantPermissionChangedNotificationData,
  IAssistantSharedNotificationData,
  ICalendarDisconnectedNotificationData,
  IChatAccessRevokedNotificationData,
  IChatSharedNotificationData,
  IConnectorPermissionChangedNotificationData,
  IDashboardAccessRevokedNotificationData,
  IDashboardPermissionChangedNotificationData,
  IDashboardSharedNotificationData,
  IIntegrationDisconnectedNotificationData,
  IKnowledgeFileAccessRevokedNotificationData,
  IKnowledgeFileSharedNotificationData,
  IMeetingAccessRevokedNotificationData,
  IMeetingFailedNotificationData,
  IMeetingProcessedNotificationData,
  IMeetingSharedNotificationData,
  IModelCreatedNotificationData,
  IOrganizationDeactivatedNotificationData,
  IOrganizationLeftNotificationData,
  IOrganizationOwnerRemovedNotificationData,
  IOrganizationOwnershipReceivedNotificationData,
  IPresentationThemeExtractionNotificationData,
  IProbeCreatedNotificationData,
  IProjectInviteNotificationData,
  IProjectLeftNotificationData,
  IProjectMemberRemovedNotificationData,
  IProjectRoleUpdatedNotificationData,
  IPromptCreatedNotificationData,
  ISelfReminderNotificationData,
  IUsageThresholdReachedNotificationData,
  IWebhookRunResultNotificationData,
  IWorkflowRunFailedNotificationData,
  TSkillNotification,
} from "@frontend-shared/types";
import { ENotificationType } from "@frontend-shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { PATHS } from "@/constants/paths";
import { useAuth } from "@/hooks/use-auth";
import { useTokenUsageEnabled } from "@/hooks/use-token-usage-enabled";
import { AGENTS_RUNS_QUERY_PARAM } from "@/pages/agents/constants";
import { EAnalyticsTab } from "@/pages/analytics/types";
import { KNOWLEDGE_TAB_PARAM } from "@/pages/knowledge/constants/tabs";
import { EKnowledgeTab } from "@/pages/knowledge/types";
import {
  EOrganizationTab,
  EProfileTab,
  ESettingsQueryParams,
  ESettingsSection,
  ETokenUsagePane,
} from "@/pages/settings/types";
import { buildModelsSettingsPath } from "@/pages/settings/utils/build-models-settings-path";

import type {
  IBuildOrganizationUrlParams,
  IBuildProjectsUrlParams,
  INotificationCardProps,
  IViewDashboardProps,
  IViewSkillProps,
} from "./types";
import { AgentAutoPausedCard } from "./variants/AgentAutoPausedCard";
import { AssistantAccessRevokedCard } from "./variants/AssistantAccessRevokedCard";
import { AssistantCreatedCard } from "./variants/AssistantCreatedCard";
import { AssistantEditedCard } from "./variants/AssistantEditedCard";
import { AssistantLeftCard } from "./variants/AssistantLeftCard";
import { AssistantPermissionChangedCard } from "./variants/AssistantPermissionChangedCard";
import { AssistantSharedCard } from "./variants/AssistantSharedCard";
import { CalendarDisconnectedCard } from "./variants/CalendarDisconnectedCard";
import { ChatAccessRevokedCard } from "./variants/ChatAccessRevokedCard";
import { ChatSharedCard } from "./variants/ChatSharedCard";
import { ConnectorPermissionChangedCard } from "./variants/ConnectorPermissionChangedCard";
import { DashboardAccessRevokedCard } from "./variants/DashboardAccessRevokedCard";
import { DashboardPermissionChangedCard } from "./variants/DashboardPermissionChangedCard";
import { DashboardSharedCard } from "./variants/DashboardSharedCard";
import { IntegrationDisconnectedCard } from "./variants/IntegrationDisconnectedCard";
import { KnowledgeFileAccessRevokedCard } from "./variants/KnowledgeFileAccessRevokedCard";
import { KnowledgeFileSharedCard } from "./variants/KnowledgeFileSharedCard";
import { MeetingAccessRevokedCard } from "./variants/MeetingAccessRevokedCard";
import { MeetingFailedCard } from "./variants/MeetingFailedCard";
import { MeetingProcessedCard } from "./variants/MeetingProcessedCard";
import { MeetingSharedCard } from "./variants/MeetingSharedCard";
import { ModelCreatedCard } from "./variants/ModelCreatedCard";
import { OrganizationDeactivatedCard } from "./variants/OrganizationDeactivatedCard";
import { OrganizationLeftCard } from "./variants/OrganizationLeftCard";
import { OrganizationOwnerRemovedCard } from "./variants/OrganizationOwnerRemovedCard";
import { OrganizationOwnershipReceivedCard } from "./variants/OrganizationOwnershipReceivedCard";
import { PresentationThemeExtractionCard } from "./variants/PresentationThemeExtractionCard";
import { ProbeCreatedCard } from "./variants/ProbeCreatedCard";
import { ProjectInviteCard } from "./variants/ProjectInviteCard";
import { ProjectLeftCard } from "./variants/ProjectLeftCard";
import { ProjectMemberRemovedCard } from "./variants/ProjectMemberRemovedCard";
import { ProjectRoleUpdatedCard } from "./variants/ProjectRoleUpdatedCard";
import { PromptCreatedCard } from "./variants/PromptCreatedCard";
import { RunFailedCard } from "./variants/RunFailedCard";
import { SelfReminderCard } from "./variants/SelfReminderCard";
import { SkillNotificationCard } from "./variants/SkillNotificationCard";
import { UsageThresholdReachedCard } from "./variants/UsageThresholdReachedCard";
import { WebhookRunResultCard } from "./variants/WebhookRunResultCard";

// NOTE: Deep-link to the projects tab of the org-settings page — the place
// where the user can see the project membership, switch into it, or take
// follow-up actions.
const buildProjectsUrl = ({
  organizationUuid,
}: IBuildProjectsUrlParams): string => {
  const params = new URLSearchParams({
    [ESettingsQueryParams.SECTION]: ESettingsSection.ORGANIZATION,
    [ESettingsQueryParams.ORGANIZATION_UUID]: organizationUuid,
    [ESettingsQueryParams.TAB]: EOrganizationTab.PROJECTS,
  });
  return `${PATHS.SETTINGS}?${params.toString()}`;
};

// NOTE: Deep-link to the General tab of the org-settings page — used by the
// ownership-transfer notifications (new + previous owner). General is where
// owner-level info (name, email, owner identity) is surfaced.
const buildOrganizationUrl = ({
  organizationUuid,
}: IBuildOrganizationUrlParams): string => {
  const params = new URLSearchParams({
    [ESettingsQueryParams.SECTION]: ESettingsSection.ORGANIZATION,
    [ESettingsQueryParams.ORGANIZATION_UUID]: organizationUuid,
    [ESettingsQueryParams.TAB]: EOrganizationTab.GENERAL,
  });
  return `${PATHS.SETTINGS}?${params.toString()}`;
};

// NOTE: Deep-link to the appearance tab of the org-settings page — where the
// presentation theme lives (NSAI-3862 extraction notifications).
const buildAppearanceUrl = ({
  organizationUuid,
}: IBuildOrganizationUrlParams): string => {
  const params = new URLSearchParams({
    [ESettingsQueryParams.SECTION]: ESettingsSection.ORGANIZATION,
    [ESettingsQueryParams.ORGANIZATION_UUID]: organizationUuid,
    [ESettingsQueryParams.TAB]: EOrganizationTab.APPEARANCE,
  });
  return `${PATHS.SETTINGS}?${params.toString()}`;
};

// NOTE: Deep-link to the calendars tab of the profile-settings page where the
// user can disconnect / reconnect calendar OAuth integrations. Mirrors the
// same URL UserMenu uses for the "Calendars" item.
const buildCalendarsUrl = (): string => {
  const params = new URLSearchParams({
    [ESettingsQueryParams.SECTION]: ESettingsSection.PROFILE,
    [ESettingsQueryParams.TAB]: EProfileTab.CALENDAR,
  });
  return `${PATHS.SETTINGS}?${params.toString()}`;
};

// NOTE: Owner-facing deep-link to the org usage-limits view (where owners raise
// the limit). NSAI-5293 — that view is now the Limits PANE of the merged Token
// Usage section, so the link names the section and the pane rather than a tab of
// its own.
const buildUsageLimitsUrl = ({
  organizationUuid,
}: IBuildOrganizationUrlParams): string => {
  const params = new URLSearchParams({
    [ESettingsQueryParams.SECTION]: ESettingsSection.ORGANIZATION,
    [ESettingsQueryParams.ORGANIZATION_UUID]: organizationUuid,
    [ESettingsQueryParams.TAB]: EOrganizationTab.TOKEN_USAGE,
    [ESettingsQueryParams.USAGE_TAB]: ETokenUsagePane.LIMITS,
  });
  return `${PATHS.SETTINGS}?${params.toString()}`;
};

// NOTE: Deep-link to the user's own token-usage view (personal-cap alerts only).
// The pane is named explicitly so the link lands on consumption rather than on
// whichever pane happens to be first for that reader.
const buildTokenUsageUrl = (): string => {
  const params = new URLSearchParams({
    [ESettingsQueryParams.SECTION]: ESettingsSection.PROFILE,
    [ESettingsQueryParams.TAB]: EProfileTab.TOKEN_USAGE,
    [ESettingsQueryParams.USAGE_TAB]: ETokenUsagePane.USAGE,
  });
  return `${PATHS.SETTINGS}?${params.toString()}`;
};

const NotificationCardComponent = ({
  notification,
  onCloseDrawer,
}: INotificationCardProps): React.JSX.Element | null => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentProjectUuid, onCurrentProjectChange } = useAuth();
  const isTokenUsageEnabled = useTokenUsageEnabled();

  // NOTE: A notification's resource (model / chat / meeting) lives inside a
  // specific project, but the user can be looking at a different project when
  // they click the CTA. If we just navigate, the destination page reads from
  // the wrong project context (e.g. /models shows the project they're in, not
  // the project where the model was created). Switch first, then navigate.
  // onCurrentProjectChange itself navigates to /chat, so the caller must
  // navigate AFTER awaiting a microtask, or pass a final destination URL.
  const switchProjectIfNeeded = useCallback(
    (targetProjectUuid: string): boolean => {
      if (!targetProjectUuid || targetProjectUuid === currentProjectUuid) {
        return false;
      }
      onCurrentProjectChange(targetProjectUuid);
      return true;
    },
    [currentProjectUuid, onCurrentProjectChange],
  );

  const handleViewProject = useCallback(
    (organizationUuid: string): void => {
      if (!organizationUuid) {
        return;
      }
      onCloseDrawer();
      navigate(buildProjectsUrl({ organizationUuid }));
    },
    [navigate, onCloseDrawer],
  );

  // NOTE: Invalidate the org cache before navigating — when the user is
  // already on the org General tab, navigate() is a same-route no-op so the
  // page never remounts and the stale isOwner/owners would otherwise persist.
  const handleViewOrganization = useCallback(
    (organizationUuid: string): void => {
      if (!organizationUuid) {
        return;
      }
      onCloseDrawer();
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
      navigate(buildOrganizationUrl({ organizationUuid }));
    },
    [navigate, onCloseDrawer, queryClient],
  );

  const handleViewAppearance = useCallback(
    (organizationUuid: string): void => {
      if (!organizationUuid) {
        return;
      }
      onCloseDrawer();
      navigate(buildAppearanceUrl({ organizationUuid }));
    },
    [navigate, onCloseDrawer],
  );

  const handleViewMeeting = useCallback(
    (meetingUuid: string): void => {
      if (!meetingUuid) {
        return;
      }
      onCloseDrawer();
      navigate(PATHS.MEETING_DETAILS.replace(":meetingUuid", meetingUuid));
    },
    [navigate, onCloseDrawer],
  );

  // NOTE: Shared chats open the read-only shared view by token — no project
  // switch is needed since access is granted by the recipient row, not by the
  // viewer's current project context.
  const handleViewSharedChat = useCallback(
    (shareToken: string): void => {
      if (!shareToken) {
        return;
      }
      onCloseDrawer();
      navigate(PATHS.SHARED_CHAT.replace(":shareToken", shareToken));
    },
    [navigate, onCloseDrawer],
  );

  // NOTE: NSAI-6940 — dashboards are project-scoped: the viewer resolves the
  // uuid against the current project, so switch into the dashboard's project
  // first. The final navigate() runs after onCurrentProjectChange's own
  // navigate(/chat), so it wins and lands the user on the dashboard.
  const handleViewDashboard = useCallback(
    ({ dashboardUuid, projectUuid }: IViewDashboardProps): void => {
      if (!dashboardUuid) {
        return;
      }
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(buildDashboardRoutePath(dashboardUuid));
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: Automations are project-scoped — the Agents page resolves the
  // ?runs= uuid against the current project's lists, so switch into the
  // automation's project first (when known) or the lookup misses and the CTA
  // becomes a no-op for cross-project reminders.
  const handleViewAgentRuns = useCallback(
    (automationUuid: string, projectUuid?: string): void => {
      if (!automationUuid) {
        return;
      }
      onCloseDrawer();
      if (projectUuid) {
        switchProjectIfNeeded(projectUuid);
      }
      navigate(
        `${PATHS.AGENTS}?${AGENTS_RUNS_QUERY_PARAM}=${encodeURIComponent(automationUuid)}`,
      );
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  const handleReconnectCalendar = useCallback((): void => {
    onCloseDrawer();
    navigate(buildCalendarsUrl());
  }, [navigate, onCloseDrawer]);

  // NOTE: Invalidate the org cache first (like handleViewOrganization) so the
  // owner-gated tab sees fresh isOwner state on same-route navigation.
  const handleViewUsageLimits = useCallback(
    (organizationUuid: string): void => {
      if (!organizationUuid) {
        return;
      }
      onCloseDrawer();
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
      navigate(buildUsageLimitsUrl({ organizationUuid }));
    },
    [navigate, onCloseDrawer, queryClient],
  );

  const handleViewUsage = useCallback((): void => {
    onCloseDrawer();
    navigate(buildTokenUsageUrl());
  }, [navigate, onCloseDrawer]);

  // NOTE: Deep-link to the per-provider integration details page so the user
  // lands directly on the reconnect flow for the integration whose token just
  // expired. Integrations are project-scoped, so we switch project first
  // (mirrors handleViewModels / handleViewPrompts) — otherwise the
  // /integrations/:provider page would render against the user's CURRENT
  // project and show empty content when the expired integration lives in a
  // different project.
  const handleViewIntegrations = useCallback(
    (provider: string, projectUuid: string): void => {
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(generatePath(PATHS.INTEGRATION_DETAILS, { provider }));
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: Switch into the model's project before navigating to its Models tab so
  // the list reflects that project (models are project-scoped). The final
  // navigate() runs after onCurrentProjectChange's own navigate(/chat), so it
  // wins and lands the user on the Models tab.
  const handleViewModels = useCallback(
    (projectUuid: string): void => {
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(buildModelsSettingsPath());
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: Switch project first so the new chat is created in the right one,
  // then deep-link to /chat?model=<uuid> — Chat.tsx reads that param and
  // pre-selects the model (mirrors the existing `?assistant=<uuid>` flow).
  const handleStartChatWithModel = useCallback(
    (modelUuid: string, projectUuid: string): void => {
      if (!modelUuid) {
        return;
      }
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(`${PATHS.CHAT}?model=${encodeURIComponent(modelUuid)}`);
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: targetProjectUuid is set for direct + project-all shares (recipient
  // needs to switch into the share's target project to see the file under
  // "Shared with me"). For PUBLIC mode it's null — the file is org-wide, so
  // we leave the recipient in their current project and just navigate.
  // Deep-link `?tab=shared-with-me` so the CTA lands on the tab that actually
  // holds the shared file instead of the default "My files" tab (NSAI-2777).
  const handleViewKnowledgeFiles = useCallback(
    (targetProjectUuid: string | null): void => {
      onCloseDrawer();
      if (targetProjectUuid) {
        switchProjectIfNeeded(targetProjectUuid);
      }
      navigate(
        `${PATHS.KNOWLEDGE}?${KNOWLEDGE_TAB_PARAM}=${EKnowledgeTab.SHARED_WITH_ME}`,
      );
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: Prompts are project-scoped — switch into the prompt's project
  // before navigating so the prompt library page shows the right list.
  const handleViewPrompts = useCallback(
    (projectUuid: string): void => {
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(PATHS.PROMPT_LIBRARY);
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: Probes live under the analytics screen on the `probes` tab — switch
  // into the probe's project and land on the right tab via `?tab=probes`.
  const handleViewProbes = useCallback(
    (projectUuid: string): void => {
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(`${PATHS.ANALYTICS}?tab=${EAnalyticsTab.PROBES}`);
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: Assistants are project-scoped — switch into the assistant's project
  // before navigating so /assistants shows the right project's list.
  const handleViewAssistants = useCallback(
    (projectUuid: string): void => {
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(PATHS.ASSISTANTS);
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  // NOTE: NSAI-6952 — skills are project-scoped: the detail page reads the
  // skill through the current project, so switch into the skill's project
  // first. The final navigate() runs after onCurrentProjectChange's own
  // navigate(/chat), so it wins and lands the user on the skill.
  const handleViewSkill = useCallback(
    ({ skillUuid, projectUuid }: IViewSkillProps): void => {
      if (!skillUuid) {
        return;
      }
      onCloseDrawer();
      switchProjectIfNeeded(projectUuid);
      navigate(generatePath(PATHS.SKILL_DETAIL, { skillUuid }));
    },
    [navigate, onCloseDrawer, switchProjectIfNeeded],
  );

  const { uuid, type, createdAt, isRead, data } = notification;

  // NOTE: Switch on the discriminator. Each branch narrows `data` via the
  // INotificationDataByType mapping in frontend-shared — new types fail to
  // type-check until a case is added here.
  switch (type) {
    case ENotificationType.PROJECT_INVITE:
      return (
        <ProjectInviteCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IProjectInviteNotificationData}
          onViewProject={handleViewProject}
        />
      );
    case ENotificationType.PROJECT_LEFT:
      return (
        <ProjectLeftCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IProjectLeftNotificationData}
          onViewProject={handleViewProject}
        />
      );
    case ENotificationType.PROJECT_MEMBER_REMOVED:
      return (
        <ProjectMemberRemovedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IProjectMemberRemovedNotificationData}
        />
      );
    case ENotificationType.PROJECT_ROLE_UPDATED:
      return (
        <ProjectRoleUpdatedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IProjectRoleUpdatedNotificationData}
          onViewProject={handleViewProject}
        />
      );
    case ENotificationType.SELF_REMINDER:
      return (
        <SelfReminderCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as ISelfReminderNotificationData}
          onViewRuns={handleViewAgentRuns}
        />
      );
    case ENotificationType.MEETING_PROCESSED:
      return (
        <MeetingProcessedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IMeetingProcessedNotificationData}
          onViewMeeting={handleViewMeeting}
        />
      );
    case ENotificationType.MEETING_FAILED:
      return (
        <MeetingFailedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IMeetingFailedNotificationData}
          onViewMeeting={handleViewMeeting}
        />
      );
    case ENotificationType.MEETING_SHARED:
      return (
        <MeetingSharedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IMeetingSharedNotificationData}
          onViewMeeting={handleViewMeeting}
        />
      );
    case ENotificationType.MEETING_ACCESS_REVOKED:
      return (
        <MeetingAccessRevokedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IMeetingAccessRevokedNotificationData}
        />
      );
    case ENotificationType.CALENDAR_DISCONNECTED:
      return (
        <CalendarDisconnectedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as ICalendarDisconnectedNotificationData}
          onReconnect={handleReconnectCalendar}
        />
      );
    case ENotificationType.INTEGRATION_DISCONNECTED:
      return (
        <IntegrationDisconnectedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          // NOTE: Discriminator narrows union member to IIntegrationDisconnectedNotificationData.
          data={data as IIntegrationDisconnectedNotificationData}
          onReconnect={handleViewIntegrations}
        />
      );
    case ENotificationType.MODEL_CREATED:
      return (
        <ModelCreatedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IModelCreatedNotificationData}
          onViewModels={handleViewModels}
          onStartChat={handleStartChatWithModel}
        />
      );
    case ENotificationType.KNOWLEDGE_FILE_SHARED:
      return (
        <KnowledgeFileSharedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IKnowledgeFileSharedNotificationData}
          onViewFiles={handleViewKnowledgeFiles}
        />
      );
    case ENotificationType.KNOWLEDGE_FILE_ACCESS_REVOKED:
      return (
        <KnowledgeFileAccessRevokedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IKnowledgeFileAccessRevokedNotificationData}
        />
      );
    case ENotificationType.PROMPT_CREATED:
      return (
        <PromptCreatedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IPromptCreatedNotificationData}
          onViewPrompts={handleViewPrompts}
        />
      );
    case ENotificationType.PROBE_CREATED:
      return (
        <ProbeCreatedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IProbeCreatedNotificationData}
          onViewProbes={handleViewProbes}
        />
      );
    case ENotificationType.ASSISTANT_CREATED:
      return (
        <AssistantCreatedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IAssistantCreatedNotificationData}
          onViewAssistants={handleViewAssistants}
        />
      );
    case ENotificationType.ASSISTANT_SHARED:
      return (
        <AssistantSharedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IAssistantSharedNotificationData}
          onViewAssistants={handleViewAssistants}
        />
      );
    case ENotificationType.ASSISTANT_LEFT:
      return (
        <AssistantLeftCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IAssistantLeftNotificationData}
          onViewAssistants={handleViewAssistants}
        />
      );
    case ENotificationType.ASSISTANT_PERMISSION_CHANGED:
      return (
        <AssistantPermissionChangedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IAssistantPermissionChangedNotificationData}
          onViewAssistants={handleViewAssistants}
        />
      );
    case ENotificationType.ASSISTANT_ACCESS_REVOKED:
      return (
        <AssistantAccessRevokedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IAssistantAccessRevokedNotificationData}
        />
      );
    case ENotificationType.ASSISTANT_EDITED:
      return (
        <AssistantEditedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IAssistantEditedNotificationData}
          onViewAssistants={handleViewAssistants}
        />
      );
    case ENotificationType.SKILL_CREATED:
    case ENotificationType.SKILL_SHARED:
    case ENotificationType.SKILL_PERMISSION_CHANGED:
    case ENotificationType.SKILL_ACCESS_REVOKED:
    case ENotificationType.SKILL_EDITED:
      return (
        <SkillNotificationCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          notification={{ type, data } as TSkillNotification}
          onViewSkill={handleViewSkill}
        />
      );
    case ENotificationType.CHAT_SHARED:
      return (
        <ChatSharedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IChatSharedNotificationData}
          onViewSharedChat={handleViewSharedChat}
        />
      );
    case ENotificationType.CHAT_ACCESS_REVOKED:
      return (
        <ChatAccessRevokedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IChatAccessRevokedNotificationData}
        />
      );
    case ENotificationType.DASHBOARD_SHARED:
      return (
        <DashboardSharedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IDashboardSharedNotificationData}
          onViewDashboard={handleViewDashboard}
        />
      );
    case ENotificationType.DASHBOARD_PERMISSION_CHANGED:
      return (
        <DashboardPermissionChangedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IDashboardPermissionChangedNotificationData}
          onViewDashboard={handleViewDashboard}
        />
      );
    case ENotificationType.DASHBOARD_ACCESS_REVOKED:
      return (
        <DashboardAccessRevokedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IDashboardAccessRevokedNotificationData}
        />
      );
    case ENotificationType.ORGANIZATION_OWNERSHIP_RECEIVED:
      return (
        <OrganizationOwnershipReceivedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IOrganizationOwnershipReceivedNotificationData}
          onViewOrganization={handleViewOrganization}
        />
      );
    case ENotificationType.ORGANIZATION_OWNER_REMOVED:
      return (
        <OrganizationOwnerRemovedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IOrganizationOwnerRemovedNotificationData}
          onViewOrganization={handleViewOrganization}
        />
      );
    case ENotificationType.ORGANIZATION_LEFT:
      return (
        <OrganizationLeftCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IOrganizationLeftNotificationData}
          onViewOrganization={handleViewOrganization}
        />
      );
    case ENotificationType.ORGANIZATION_DEACTIVATED:
      return (
        <OrganizationDeactivatedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IOrganizationDeactivatedNotificationData}
        />
      );
    case ENotificationType.PRESENTATION_THEME_READY:
      return (
        <PresentationThemeExtractionCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IPresentationThemeExtractionNotificationData}
          isSuccess
          onViewAppearance={handleViewAppearance}
        />
      );
    case ENotificationType.PRESENTATION_THEME_FAILED:
      return (
        <PresentationThemeExtractionCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IPresentationThemeExtractionNotificationData}
          isSuccess={false}
          onViewAppearance={handleViewAppearance}
        />
      );
    case ENotificationType.USAGE_THRESHOLD_REACHED:
      return (
        <UsageThresholdReachedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IUsageThresholdReachedNotificationData}
          isTokenUsageEnabled={isTokenUsageEnabled}
          onViewUsageLimits={handleViewUsageLimits}
          onViewUsage={handleViewUsage}
        />
      );
    case ENotificationType.AGENT_AUTO_PAUSED:
      return (
        <AgentAutoPausedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IAgentAutoPausedNotificationData}
          onViewRuns={handleViewAgentRuns}
        />
      );
    case ENotificationType.WEBHOOK_RUN_SUCCEEDED:
      return (
        <WebhookRunResultCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IWebhookRunResultNotificationData}
          isSuccess
          onViewRuns={handleViewAgentRuns}
        />
      );
    case ENotificationType.WEBHOOK_RUN_FAILED:
      return (
        <WebhookRunResultCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IWebhookRunResultNotificationData}
          isSuccess={false}
          onViewRuns={handleViewAgentRuns}
        />
      );
    case ENotificationType.AGENT_RUN_FAILED:
      return (
        <RunFailedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          notification={{
            type,
            data: data as IAgentRunFailedNotificationData,
          }}
          onViewRuns={handleViewAgentRuns}
        />
      );
    case ENotificationType.WORKFLOW_RUN_FAILED:
      return (
        <RunFailedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          notification={{
            type,
            data: data as IWorkflowRunFailedNotificationData,
          }}
          onViewRuns={handleViewAgentRuns}
        />
      );
    case ENotificationType.CONNECTOR_PERMISSION_CHANGED:
      return (
        <ConnectorPermissionChangedCard
          uuid={uuid}
          createdAt={createdAt}
          isRead={isRead}
          data={data as IConnectorPermissionChangedNotificationData}
          // NOTE: Same handler as the reconnect CTA — both land on the
          // per-provider integration page, where the approval settings card
          // for this connector lives.
          onViewConnectorSettings={handleViewIntegrations}
        />
      );
    default:
      return null;
  }
};

export const NotificationCard = memo(NotificationCardComponent);
